/**
 * Fiscal/billing profile. Required for owner commission invoices; optional
 * for guests and reusable as contract identity defaults.
 *
 * Keep validation logic in sync with functions/src/shared/billingProfile.ts
 */

/** Legacy Firestore value retained while existing profiles migrate. */
export type FiscalRegime = 'empresa_autonomo' | 'particular';
export type BillingEntityType = 'persona_fisica' | 'persona_juridica';
export type PlatformIssuerType = 'self_employed' | 'company';

export interface BillingAddress {
  line1: string;
  postalCode: string;
  city: string;
  province: string;
  country: 'ES';
}

export interface BillingProfile {
  legalName: string;
  taxId: string;
  entityType: BillingEntityType;
  actsAsBusiness: boolean;
  /** Derived compatibility mirror for previously deployed clients. */
  fiscalRegime: FiscalRegime;
  address: BillingAddress;
  updatedAt?: number;
}

export const BILLING_ENTITY_TYPE_LABELS: Record<BillingEntityType, string> = {
  persona_fisica: 'Persona física',
  persona_juridica: 'Persona jurídica',
};

export const DEFAULT_BILLING_COUNTRY = 'ES' as const;

/** Issuer data for platform-issued invoices (stored in /config/globalSettings). */
export interface PlatformBillingSettings {
  legalName: string;
  taxId: string;
  address: BillingAddress;
  issuerType: PlatformIssuerType;
  withholdingEnabled: boolean;
  /** IVA repercutido en facturas (porcentaje, ej. 21). */
  vatRatePercent: number;
  /**
   * Retención IRPF del emisor autónomo cuando el destinatario debe retener.
   * Se ignora cuando issuerType es company o withholdingEnabled es false.
   */
  irpfWithholdingPercent: number;
  updatedAt?: number;
}

export const DEFAULT_PLATFORM_VAT_RATE_PERCENT = 21;
export const DEFAULT_PLATFORM_IRPF_PERCENT = 15;

export const PLATFORM_IRPF_PRESETS: { label: string; value: number }[] = [
  { label: 'Sin retención (0 %)', value: 0 },
  { label: 'IRPF 7 %', value: 7 },
  { label: 'IRPF 15 %', value: 15 },
];
